/**
 * Reads public Bluesky data straight from the browser.
 *
 * `public.api.bsky.app` sends `access-control-allow-origin: *`, so the fetch
 * happens in the visitor's own browser rather than through our Worker. That is
 * not a shortcut — it is the better design: no server cost, no rate limiting to
 * build, no cache to invalidate, no abuse surface, and the request reaches
 * Bluesky from the person actually asking rather than from us on their behalf.
 *
 * Read-only and unauthenticated. No app password is involved and none should
 * ever be asked for here.
 */

import { z } from "zod";

const API = "https://public.api.bsky.app/xrpc";

/**
 * Bluesky's responses are third-party data, so they are parsed rather than
 * asserted. A shape change on their side then surfaces here as a clear failure
 * instead of `undefined` flowing into the analysis and producing a confident
 * chart of nothing.
 *
 * Deliberately loose: only the fields this tool reads are described, and counts
 * default to 0 because a brand-new post legitimately has none.
 */
const profileSchema = z.object({
  handle: z.string().optional(),
  displayName: z.string().nullish(),
  avatar: z.string().nullish(),
  followersCount: z.number().optional(),
});

const feedSchema = z.object({
  cursor: z.string().optional(),
  feed: z
    .array(
      z.object({
        reason: z.unknown().optional(),
        post: z
          .object({
            likeCount: z.number().optional(),
            repostCount: z.number().optional(),
            replyCount: z.number().optional(),
            record: z
              .object({
                createdAt: z.string().optional(),
                reply: z.unknown().optional(),
              })
              .optional(),
          })
          .optional(),
      }),
    )
    .optional(),
});

export type FetchedPost = {
  createdAt: string;
  /** Likes + reposts + replies, the single number the timing analysis scores. */
  engagement: number;
  /**
   * The same three counts kept apart, because a reposts-heavy account and a
   * replies-heavy one behave differently and the sum hides which you are.
   * Additive: anything reading only `engagement` is unaffected.
   */
  likes: number;
  reposts: number;
  replies: number;
};

export type FetchedAccount = {
  handle: string;
  displayName: string | null;
  avatar: string | null;
  followers: number;
  posts: FetchedPost[];
};

export class BlueskyLookupError extends Error {
  readonly kind: "not-found" | "network" | "empty";

  constructor(kind: "not-found" | "network" | "empty", message: string) {
    super(message);
    this.kind = kind;
    this.name = "BlueskyLookupError";
  }
}

/** `@alice.bsky.social`, `alice.bsky.social` and a profile URL all mean the same account. */
export function normaliseHandle(input: string): string {
  const trimmed = input.trim();
  const fromUrl = /bsky\.app\/profile\/([^/?#\s]+)/.exec(trimmed);
  const raw = fromUrl ? fromUrl[1] : trimmed;
  return raw.replace(/^@/, "").toLowerCase();
}

async function call<T>(
  schema: z.ZodType<T>,
  path: string,
  params: Record<string, string>,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API}/${path}?${new URLSearchParams(params)}`);
  } catch {
    throw new BlueskyLookupError(
      "network",
      "Could not reach Bluesky. Check your connection and try again.",
    );
  }
  if (response.status === 400 || response.status === 404) {
    throw new BlueskyLookupError(
      "not-found",
      "No Bluesky account with that handle. Check the spelling — it usually ends in .bsky.social.",
    );
  }
  if (!response.ok) {
    throw new BlueskyLookupError(
      "network",
      "Bluesky returned an error. It may be rate-limiting; wait a moment and try again.",
    );
  }
  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    throw new BlueskyLookupError(
      "network",
      "Bluesky returned something this tool did not recognise. Their API may have changed.",
    );
  }
  return parsed.data;
}

/**
 * Profile plus up to `maxPosts` recent original posts.
 *
 * Reposts and replies are excluded: a repost carries someone else's timing, and
 * replies have entirely different engagement dynamics. Both would corrupt the
 * question being asked.
 */
export async function fetchAccount(
  handleInput: string,
  maxPosts = 300,
): Promise<FetchedAccount> {
  const handle = normaliseHandle(handleInput);
  if (!handle) {
    throw new BlueskyLookupError(
      "not-found",
      "Enter a Bluesky handle to begin.",
    );
  }

  const profile = await call(profileSchema, "app.bsky.actor.getProfile", {
    actor: handle,
  });

  const posts: FetchedPost[] = [];
  let cursor: string | undefined;

  while (posts.length < maxPosts) {
    const base = {
      actor: handle,
      limit: "100",
      filter: "posts_no_replies",
    };
    // Absent on the first page; sending an empty cursor is not the same thing.
    const params = cursor ? { ...base, cursor } : base;

    const page = await call(feedSchema, "app.bsky.feed.getAuthorFeed", params);

    for (const item of page.feed ?? []) {
      if (item.reason) continue; // a repost carries someone else's timing
      const post = item.post;
      const record = post?.record;
      if (!post || !record?.createdAt) continue;
      if (record.reply) continue; // replies engage differently
      const likes = post.likeCount ?? 0;
      const reposts = post.repostCount ?? 0;
      const replies = post.replyCount ?? 0;
      posts.push({
        createdAt: record.createdAt,
        engagement: likes + reposts + replies,
        likes,
        reposts,
        replies,
      });
    }

    cursor = page.cursor;
    if (!cursor || (page.feed ?? []).length === 0) break;
  }

  if (posts.length === 0) {
    throw new BlueskyLookupError(
      "empty",
      "That account has no public original posts to analyse — only replies or reposts, or nothing yet.",
    );
  }

  return {
    handle: profile.handle ?? handle,
    displayName: profile.displayName ?? null,
    avatar: profile.avatar ?? null,
    followers: profile.followersCount ?? 0,
    posts: posts.slice(0, maxPosts),
  };
}
